import {AuthClientSingleton} from './auth';
import {extractError} from '../api/nFetch';

const POST = 'POST';
const GET = 'GET';
const PUT = 'PUT';

export interface ApiResponse<T> {
    status: number;
    error?: string;
    data?: T;
}

const getHeaders = () => {
    let headers: Record<string, string> = {
        'Content-Type': 'application/json',
    };

    const authToken = AuthClientSingleton.getToken();
    if (authToken) headers['Authorization'] = `${authToken.kind} ${authToken.accessToken}`;

    return headers;
};

const get = async (url: string) => {
    const headers = getHeaders();

    const response = await fetch(url, {
        method: GET,
        headers,
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(extractError(data, response));
    }
    return data;
};

async function apiGet<T>(url: string): Promise<ApiResponse<T>> {
    const headers = getHeaders();
    try {
        const response = await fetch(url, {method: GET, headers: headers});

        if (!response.ok) {
            const errorText = await response.text();
            return {
                status: response.status,
                error: errorText,
            };
        }
        const data: T = await response.json();
        return {
            status: 200,
            data: data,
        };
    } catch (e: any) {
        return {
            status: 500,
            error: e.message,
        };
    }
}

async function apiPost<T>(url: string, body: any, additionalHeaders = {}): Promise<ApiResponse<T>> {
    const headers = {
        ...getHeaders(),
        ...additionalHeaders,
    };

    body = JSON.stringify(body);

    try {
        const response = await fetch(url, {
            method: POST,
            headers,
            body: body,
        });

        if (!response.ok) {
            const res = await response.json();
            if (Array.isArray(res)) {
                return {
                    status: response.status,
                    error: res[0],
                };
            } else {
                return {
                    status: response.status,
                    error: res.message,
                };
            }
        }
        const data: T = await response.json();
        return {
            status: response.status,
            data: data,
        };
    } catch (e: any) {
        return {
            status: 500,
            error: e.message,
        };
    }
}

async function apiPut<T>(url: string, body: any, additionalHeaders = {}): Promise<ApiResponse<T>> {
    const headers = {
        ...getHeaders(),
        ...additionalHeaders,
    };

    body = JSON.stringify(body);

    try {
        const response = await fetch(url, {
            method: PUT,
            headers,
            body: body,
        });

        if (!response.ok) {
            const res = await response.json();
            if (Array.isArray(res)) {
                return {
                    status: response.status,
                    error: res[0],
                };
            } else {
                return {
                    status: response.status,
                    error: res.message,
                };
            }
        }
        const data: T = await response.json();
        return {
            status: response.status,
            data: data,
        };
    } catch (e: any) {
        return {
            status: 500,
            error: e.message,
        };
    }
}

const post = async (url: string, body: any, additionalHeaders = {}) => {
    const headers = {
        ...getHeaders(),
        ...additionalHeaders,
    };

    body = JSON.stringify(body);

    const response = await fetch(url, {
        method: POST,
        headers,
        body: body,
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(extractError(data, response));
    }
    return data;
};

const postForm = async (url: string, body: FormData, additionalHeaders = {}) => {
    const headers: Record<string, string> = {
        ...getHeaders(),
        ...additionalHeaders,
    };
    // fetch will set the correct content type for us
    delete headers['Content-Type'];

    const response = await fetch(url, {
        method: POST,
        headers: headers,
        body: body,
    });
    const data = await response.json();
    if (!response.ok) {
        if (!data.err) {
            if (typeof data === 'string') {
                throw new Error(data);
            } else if (Array.isArray(data)) {
                throw new Error(data[0]);
            } else {
                const msgKeys = Object.keys(data);
                if (msgKeys.length > 0) {
                    throw new Error(data[msgKeys[0]]);
                } else {
                    console.error('Unknown error on file upload', data);
                    throw new Error('something went wrong');
                }
            }
        }
        throw new Error(data.err || 'HTTP error');
    }
    return data;
};

export {get, post, postForm, getHeaders, apiGet, apiPost, apiPut};
